import { spawnSync } from 'node:child_process';
import pc from 'picocolors';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, human, type Io } from '../output.js';

interface CheckResult {
  name: string;
  ok: boolean;
  hard: boolean;
  detail: string;
}

export async function runPreflight(options: {
  json?: boolean;
  adminUrl?: string;
}): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const checks: CheckResult[] = [];

  // 1. Node >= 20 (hard).
  const major = Number(process.versions.node.split('.')[0]);
  checks.push({
    name: 'node',
    ok: major >= 20,
    hard: true,
    detail:
      major >= 20
        ? `v${process.versions.node}`
        : `v${process.versions.node} — need Node 20+ (https://nodejs.org)`,
  });

  // 2. git (soft — only needed for pushing code changes).
  const gitRes = spawnSync('git', ['--version'], { encoding: 'utf8', shell: false });
  const gitOk = !gitRes.error && gitRes.status === 0;
  checks.push({
    name: 'git',
    ok: gitOk,
    hard: false,
    detail: gitOk ? gitRes.stdout.trim() : 'not found — install from https://git-scm.com',
  });

  // 3. Profile (soft — preflight should be runnable before first login).
  const resolved = resolveProfile(options.adminUrl);
  io.productName = resolved?.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;
  checks.push({
    name: 'profile',
    ok: resolved !== null,
    hard: false,
    detail: resolved
      ? `signed in as ${resolved.profile.email} (${resolved.adminUrl})`
      : 'not signed in — run `ghosty login`',
  });

  // 4. Admin reachability (hard when we know which admin to reach).
  const adminUrl = options.adminUrl ?? resolved?.adminUrl;
  if (adminUrl) {
    let detail: string;
    let okHealth = false;
    try {
      const res = await fetch(new URL('/health', `${adminUrl}/`), {
        signal: AbortSignal.timeout(10_000),
      });
      okHealth = res.ok;
      detail = okHealth ? `${adminUrl}/health → ${res.status}` : `${adminUrl}/health → HTTP ${res.status}`;
    } catch (err) {
      detail = `${adminUrl} unreachable: ${(err as Error).message}`;
    }
    checks.push({ name: 'network', ok: okHealth, hard: true, detail });
  } else {
    checks.push({
      name: 'network',
      ok: true,
      hard: false,
      detail: 'skipped (no admin URL known yet — sign in first)',
    });
  }

  const hardFailures = checks.filter((c) => c.hard && !c.ok);
  const okAll = hardFailures.length === 0;

  if (io.json) {
    emitJson({ ok: okAll, checks });
  } else {
    for (const c of checks) {
      const icon = c.ok ? pc.green('✔') : c.hard ? pc.red('✘') : pc.yellow('○');
      human(io, `${icon} ${c.name.padEnd(8)} ${c.detail}`);
    }
    human(io);
    human(
      io,
      okAll
        ? `${pc.green('ready')} — ${io.productName} CLI is good to go.`
        : pc.red('Some required checks failed. Fix them and re-run `ghosty preflight`.'),
    );
  }
  process.exit(okAll ? 0 : 1);
}
